// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

// The offline write queue (#22): enqueue() stores a write locally while
// offline; replayOutbox() sends queued writes through the real axios
// instance (so the existing 401/refresh interceptor still applies) once
// back online. See docs/design-notes.md §5 for the design this revives/
// declines from the original (superseded) proposal.

import { transactionAPI } from '../api/services/budget/transactionService';
import { db, enqueueOutboxItem, getPendingOutboxItems } from './db';

// True for a request that never reached the server — same definition as
// AuthContext's, duplicated here to avoid a context-module → offline-module
// dependency for one tiny function. Keep both in sync if this ever changes.
export function isNetworkError(err) {
  return !err?.response;
}

const listeners = new Set();
export function onReplayProgress(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function emitProgress(detail) {
  listeners.forEach((cb) => cb(detail));
}

function extractErrorMessage(err) {
  const data = err?.response?.data;
  return (
    data?.detail ||
    (data && typeof data === 'object' && Object.values(data)[0]?.[0]) ||
    'Sync failed.'
  );
}

// entity/op → the server call to make when replaying. Only 'transaction' is
// supported for #22's MVP scope (see the plan: category/payment-mode CRUD
// and imports are explicitly out of scope).
const HANDLERS = {
  transaction: {
    create: (payload) => transactionAPI.create(payload),
    update: (id, payload) => transactionAPI.update(id, payload),
    delete: (id) => transactionAPI.delete(id),
  },
};

export async function enqueue({ entity, op, targetId = null, payload = null, dependsOnClientRef = null }) {
  const clientRef = op === 'create' ? crypto.randomUUID() : null;
  const finalPayload = clientRef ? { ...payload, client_ref: clientRef } : payload;
  const localId = await enqueueOutboxItem({
    entity,
    op,
    targetId,
    payload: finalPayload,
    clientRef,
    dependsOnClientRef,
  });
  return { localId, clientRef, payload: finalPayload };
}

export const getPendingCount = () => db.outbox.where('status').equals('pending').count();
export const getFailedItems = () => db.outbox.where('status').equals('failed').sortBy('createdAt');

// Re-opens a failed item for another attempt (used by "Edit and retry" once
// the user has fixed whatever made it fail) — or just re-queues as-is.
export async function retryFailedItem(localId, newPayload) {
  await db.outbox.update(localId, {
    status: 'pending',
    error: null,
    ...(newPayload ? { payload: newPayload } : {}),
  });
}

export async function discardOutboxItem(localId) {
  await db.outbox.delete(localId);
}

let replaying = false;

// Strict FIFO, sequential — never parallel. An update/delete queued against
// a not-yet-synced create must wait for that create to get a real server id
// (resolvedIds), and a network failure must stop the whole batch rather
// than race ahead while connectivity is presumably still down.
export async function replayOutbox() {
  if (replaying) return;
  replaying = true;
  try {
    const items = await getPendingOutboxItems();
    const resolvedIds = new Map(); // clientRef -> server id, this pass only
    let processed = 0;

    for (const item of items) {
      processed += 1;
      emitProgress({ processing: processed, total: items.length });

      const handler = HANDLERS[item.entity]?.[item.op];
      if (!handler) {
        await db.outbox.update(item.localId, {
          status: 'failed',
          error: `Unknown ${item.entity}/${item.op}`,
        });
        continue;
      }

      let targetId = item.targetId;
      if (!targetId && item.dependsOnClientRef) {
        targetId = resolvedIds.get(item.dependsOnClientRef);
        if (!targetId) {
          // The create this depends on never resolved in this pass — either
          // still pending from an earlier stop, or it failed outright. Not
          // a network error, so don't block the rest of the queue; surface
          // it for manual review instead of silently dropping it.
          await db.outbox.update(item.localId, {
            status: 'failed',
            error: 'Depends on a transaction that has not synced yet.',
          });
          continue;
        }
      }

      try {
        const res =
          item.op === 'create'
            ? await handler(item.payload)
            : item.op === 'delete'
              ? await handler(targetId)
              : await handler(targetId, item.payload);

        if (item.op === 'create' && item.clientRef) {
          resolvedIds.set(item.clientRef, res.data.id);
        }
        await db.outbox.delete(item.localId);
      } catch (err) {
        if (isNetworkError(err)) {
          await db.outbox.update(item.localId, { attempts: (item.attempts ?? 0) + 1 });
          break; // connectivity is presumably still down — stop, wait for next trigger
        }
        if (err.response?.status === 404 && item.op === 'delete') {
          // Goal already achieved.
          await db.outbox.delete(item.localId);
          continue;
        }
        if (err.response?.status === 404) {
          await db.outbox.update(item.localId, {
            status: 'failed',
            error: 'This transaction no longer exists.',
          });
          continue;
        }
        await db.outbox.update(item.localId, {
          status: 'failed',
          error: extractErrorMessage(err),
        });
      }
    }
  } finally {
    replaying = false;
    emitProgress({ done: true });
  }
}
